import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import MicrositeRenderer from "./MicrositeRenderer.js";
import axios from "axios";

const MicrositePage = () => {
  const { organizationId, eventId } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "true";

  const [loading, setLoading] = useState(true);
  const [eventData, setEventData] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      setBlocked(false);

      try {
        // Attempt normal fetch first (preview=false)
        const res = await axios.get(
          "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/getEvent",
          {
            headers: { "organization-id": organizationId },
            params: {
              eventId,
              preview: isPreview ? "true" : "false",
            },
          }
        );

        const event = res.data?.eventData;
        if (!event) {
          setError("Event not found.");
          setBlocked(true);
          return;
        }

        if (!isPreview && event.visibleToPublic !== true) {
          setBlocked(true);
          setEventData(event);
          return;
        }

        setEventData(event);
      } catch (e) {
        const status = e?.response?.status;

        // Public microsites will 401/403 because getEvent requires auth unless preview=true.
        // Retry with preview=true but still gate visibleToPublic on the client.
        if (!isPreview && (status === 401 || status === 403)) {
          try {
            const previewRes = await axios.get(
              "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/getEvent",
              {
                headers: { "organization-id": organizationId },
                params: {
                  eventId,
                  preview: "true",
                },
              }
            );

            const event = previewRes.data?.eventData;
            if (!event) {
              setError("Event not found.");
              setBlocked(true);
              return;
            }

            if (event.visibleToPublic !== true) {
              setBlocked(true);
              setEventData(event);
              return;
            }

            setEventData(event);
            return;
          } catch (retryErr) {
            console.error("Microsite preview fallback failed:", retryErr);
          }
        }

        console.error("Microsite load failed:", e);
        setError("Failed to load microsite.");
        setBlocked(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [organizationId, eventId, isPreview]);

  if (loading) return <div style={{ padding: 32 }}>Loading microsite…</div>;

  if (blocked) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <h2>{eventData?.eventName || "This event is not published yet."}</h2>
        {error && <p>{error}</p>}
        {!isPreview && (
          <p>
            This microsite isn’t live yet. If you’re an admin, open it in preview
            mode from Kinova.
          </p>
        )}
      </div>
    );
  }

  return (
    <MicrositeRenderer
      event={eventData}
      preview={isPreview}
      organizationId={organizationId}
    />
  );
};

export default MicrositePage;